import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';
import { LessonStatus, InformationText, PaginationContainer, PaginationButton, PaginationInfo, ManageButton, LessonBadgeWithIcon } from './shared/CommonElements';
import { getBackNavigationPath, getBackButtonText } from '../utils/adminUtils';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 38px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  z-index: 1000;
  margin: 0 auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const LessonCard = styled.div`
  background: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.13)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.08)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.04)';
      default:
        return '#f5f5f5';
    }
  }};
  border: 1px solid ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#e0e0e0';
    }
  }};
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const LessonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const LessonTopic = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  flex: 1;
  min-width: 0;
`;



const LessonDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1.1rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const CategoryBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.2)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.2)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.2)';
      default:
        return 'rgba(158, 158, 158, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#0277bd';
      case 'ADULTS':
        return '#8e24aa';
      case 'SPECIAL':
        return '#c2185b';
      default:
        return '#616161';
    }
  }};
  border: 1px solid ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#9e9e9e';
    }
  }};
`;

const AdditionalInfo = styled.div`
  background: #f8f9fa;
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  margin-top: 12px;
  border-left: 4px solid ${theme.colors.accent1};
`;

const CancellationReason = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  color: ${theme.colors.primary};
  margin-top: 5px;
`;

const LessonSummary = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  color: ${theme.colors.primary};
  margin-top: 5px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: #fff;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 20px;
  position: relative;
  
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const SkeletonCard = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-bottom: 20px;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SkeletonTitle = styled.div`
  height: 24px;
  width: 200px;
  background: #e0e0e0;
  border-radius: 4px;
`;

const SkeletonBadge = styled.div`
  height: 20px;
  width: 80px;
  background: #e0e0e0;
  border-radius: 12px;
`;

const SkeletonDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SkeletonDetailItem = styled.div`
  height: 16px;
  width: 100%;
  background: #e0e0e0;
  border-radius: 4px;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e9ecef;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 500;
  color: ${theme.colors.primary};
  cursor: pointer;
`;

const FilterText = styled.span`
  @media (max-width: ${mobileBreakpoint}px) {
    display: none;
  }
`;

const FilterTextMobile = styled.span`
  display: none;
  
  @media (max-width: ${mobileBreakpoint}px) {
    display: inline;
  }
`;

const FilterCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${theme.colors.accent1};
  cursor: pointer;
`;

const PastLessonsPage = () => {
  const { token, isAuthenticated, hasRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterWithoutSummary, setFilterWithoutSummary] = useState(false);
  const lessonsPerPage = 5;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated() || !token) return;
    
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Read URL parameters and determine filter state
        const withoutSummaryParam = searchParams.get('withoutSummary');
        const shouldFilterWithoutSummary = withoutSummaryParam === 'true';
        
        // Update filter state if it differs from URL parameter
        if (shouldFilterWithoutSummary !== filterWithoutSummary) {
          setFilterWithoutSummary(shouldFilterWithoutSummary);
        }

        const url = new URL(config.getApiUrl('/api/lessons/past-lessons'));
        if (shouldFilterWithoutSummary) {
          url.searchParams.append('withoutSummary', 'true');
        }

        const response = await fetch(url.toString(), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setLessons(data.lessons || []);
          setCurrentPage(1); // Reset to first page when filter changes
        } else {
          setError('Fehler beim Laden der vergangenen Stunden');
        }
      } catch (err) {
        setError('Verbindungsfehler');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isAuthenticated, navigate, searchParams]);

  // Handle filter changes from checkbox (separate from URL parameter changes)
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated() || !token) return;
    
      try {
        const url = new URL(config.getApiUrl('/api/lessons/past-lessons'));
        if (filterWithoutSummary) {
          url.searchParams.append('withoutSummary', 'true');
        }

        const response = await fetch(url.toString(), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setLessons(data.lessons || []);
          setCurrentPage(1); // Reset to first page when filter changes
        } else {
          setError('Fehler beim Laden der vergangenen Stunden');
        }
      } catch (err) {
        setError('Verbindungsfehler');
        console.error('Error fetching data:', err);
      }
    };

    // Only fetch if this is a manual filter change (not initial load)
    // We can detect this by checking if we're not in the initial loading state
    if (!loading) {
      fetchData();
    }
  }, [filterWithoutSummary, token, isAuthenticated]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'DONE':
        return 'Abgeschlossen';
      case 'CANCELLED':
        return 'Storniert';
      case 'RESCHEDULE_REQUESTED':
        return 'Verschiebung angefragt';
      default:
        return status;
    }
  };

  const getCategoryText = (category) => {
    switch (category) {
      case 'CHILDREN':
        return 'Kinder';
      case 'ADULTS':
        return 'Erwachsene';
      case 'SPECIAL':
        return 'Spezial';
      default:
        return category || 'Sonstiges';
    }
  };

  const renderSkeletonCards = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <SkeletonCard key={index}>
        <SkeletonHeader>
          <SkeletonTitle />
          <SkeletonBadge />
        </SkeletonHeader>
        <SkeletonDetails>
          <SkeletonDetailItem />
          <SkeletonDetailItem />
          <SkeletonDetailItem />
        </SkeletonDetails>
      </SkeletonCard>
    ));
  };

  // Pagination logic
  const totalPages = Math.ceil(lessons.length / lessonsPerPage);
  const startIndex = (currentPage - 1) * lessonsPerPage;
  const endIndex = startIndex + lessonsPerPage;
  const currentLessons = lessons.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleManageLesson = (lessonId) => {
    navigate(`/manage-lesson/${lessonId}`);
  };

  if (loading) {
    return (
      <PageLayout>
        <ContentContainer>
          {renderSkeletonCards()}
        </ContentContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <LessonBadgeWithIcon />
          <MainTitle>Vergangene Stunden</MainTitle>
        </HeroTitle>
        <Subtitle>Übersicht deiner bisherigen Stunden</Subtitle>
      </HeroSection>
      
      <ContentContainer>
        <BackButton onClick={() => navigate(getBackNavigationPath())}>
          ← Zurück zu {getBackButtonText()}
        </BackButton>

        {hasRole('ADMIN') && (
          <FilterContainer>
            <FilterLabel>
              <FilterCheckbox
                type="checkbox"
                checked={filterWithoutSummary}
                onChange={(e) => setFilterWithoutSummary(e.target.checked)}
              />
              <FilterText>Nur Stunden ohne Zusammenfassung anzeigen</FilterText>
              <FilterTextMobile>Stunden ohne Zusammenfassung</FilterTextMobile>
            </FilterLabel>
          </FilterContainer>
        )}

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            border: '1px solid #ffcdd2',
            borderRadius: theme.borderRadius.medium,
            padding: '16px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {lessons.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>📚</EmptyStateIcon>
            <EmptyStateText>
              {filterWithoutSummary 
                ? 'Alle vergangenen Stunden haben bereits eine Zusammenfassung.' 
                : 'Du hast noch keine vergangenen Stunden.'
              }
            </EmptyStateText>
          </EmptyState>
        ) : (
          <>
            {currentLessons.map((lesson, index) => (
            <LessonCard key={lesson.id || index} category={lesson.category}>
              <LessonHeader>
                <LessonTopic>
                  {hasRole('ADMIN') && lesson.userFirstName ? 
                  lesson.isGroupLesson ? (
                    <>
                      {lesson.topic || 'Stunde'}
                    </>
                  ) : (
                    <>
                      {lesson.topic || 'Stunde'} mit {lesson.userFirstName}
                    </>
                  ) : lesson.isTrialLesson ? (
                    <>
                      Probestunde {lesson.userName ? `mit ${lesson.userFirstName}` : ''}
                    </>
                  ) : (
                    lesson.topic || 'Stunde'
                  )}
                </LessonTopic>
              </LessonHeader>
              
              <LessonDetails>
                <DetailItem>
                  <DetailLabel>Datum</DetailLabel>
                  <DetailValue>{formatDate(lesson.date)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Zeit</DetailLabel>
                  <DetailValue>{lesson.time}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Status</DetailLabel>
                  <LessonStatus status={getStatusText(lesson.status)}>{getStatusText(lesson.status)}</LessonStatus>
                </DetailItem>
              </LessonDetails>
              {lesson.additionalInfo && (
                <>
                  <DetailLabel>Zusätzliche Informationen</DetailLabel>
                  <LessonSummary>
                    <InformationText style={{ marginBottom: '0px', fontSize: '0.9rem' }}>
                      {lesson.isGroupLesson && typeof lesson.additionalInfo === 'object' ? (
                        // Group lesson: Display consolidated section with all users' additionalInfo
                        Object.entries(lesson.additionalInfo).map(([username, userAdditionalInfo], index) => {
                          const userDisplayName = lesson.userFirstName && Array.isArray(lesson.userFirstName) && lesson.userFirstName[index] 
                            ? lesson.userFirstName[index] 
                            : username;
                          
                          const info = userAdditionalInfo || 'Keine zusätzlichen Informationen angegeben.';
                          
                          return (
                            <div key={username} style={{ marginBottom: index < Object.entries(lesson.additionalInfo).length - 1 ? '16px' : '0' }}>
                              <strong>{userDisplayName}: </strong>
                              {info}
                            </div>
                          );
                        })
                      ) : (
                        // Non-group lesson: Display single additionalInfo
                        lesson.additionalInfo
                      )}
                    </InformationText>
                  </LessonSummary>
                </>
              )}

              {lesson.lessonSummary && (
                <>
                  <DetailLabel>{lesson.isTrialLesson ? 'Zusammenfassung der Probestunde' : 'Stundenübersicht'}</DetailLabel>
                  <LessonSummary>
                    <InformationText style={{ marginBottom: '0px', fontSize: '0.9rem' }}>
                      {lesson.isGroupLesson && typeof lesson.lessonSummary === 'object' ? (
                        // Group lesson: Display consolidated section with all users' lesson summaries
                        Object.entries(lesson.lessonSummary).map(([username, userLessonSummary], index) => {
                          const userDisplayName = lesson.userFirstName && Array.isArray(lesson.userFirstName) && lesson.userFirstName[index] 
                            ? lesson.userFirstName[index] 
                            : username;
                          
                          const summary = userLessonSummary || 'Noch nicht verfügbar.';
                          
                          return (
                            <div key={username} style={{ marginBottom: index < Object.entries(lesson.lessonSummary).length - 1 ? '16px' : '0' }}>
                              <strong>{userDisplayName}: </strong>
                              {summary}
                            </div>
                          );
                        })
                      ) : (
                        // Non-group lesson: Display single lesson summary
                        lesson.lessonSummary
                      )}
                    </InformationText>
                  </LessonSummary>
                </>
              )}

              {lesson.status === 'CANCELLED' && (lesson.cancellationReasonLong || lesson.cancellationReason) && (
                <>
                  <DetailLabel>Stornierungsgrund</DetailLabel>
                  <CancellationReason>
                    <InformationText style={{ marginBottom: '0px', fontSize: '0.9rem' }}>
                      {lesson.cancellationReasonLong || lesson.cancellationReason}
                    </InformationText>
                  </CancellationReason>
                </>
              )}
              
              {/* Admin manage button */}
              {hasRole('ADMIN') && (
                <div style={{ 
                  marginTop: '16px', 
                  display: 'flex', 
                  justifyContent: 'flex-end' 
                }}>
                  <ManageButton onClick={() => handleManageLesson(lesson.id)}>
                    Stunde verwalten
                  </ManageButton>
                </div>
              )}
            </LessonCard>
          ))}
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={handlePreviousPage} 
                disabled={currentPage === 1}
              >
                ←
              </PaginationButton>
              
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                <PaginationButton
                  key={page}
                  onClick={() => handlePageChange(page)}
                  active={currentPage === page}
                >
                  {page}
                </PaginationButton>
              ))}
              
              <PaginationButton 
                onClick={handleNextPage} 
                disabled={currentPage === totalPages}
              >
                →
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
        )}
      </ContentContainer>
    </PageLayout>
  );
};

export default PastLessonsPage; 